import { apiFetch } from './api';

/**
 * ATENCAO ao montar caminho aqui: `apiFetch` ja prepende `API_BASE`
 * (`/api/v1`, ou o valor de VITE_API_URL). Este arquivo prefixava `/api/v1/`
 * a mao em TODAS as 11 chamadas, produzindo `/api/v1/api/v1/strategist/...`.
 *
 * O efeito nao era um bug pontual: as quatro telas do Estrategista (Blueprint
 * Diario, Playbook, Kanban Estrategico e a configuracao) e o chat do Copiloto
 * NUNCA funcionaram neste build — ficavam presas em "Carregando..." porque
 * cada chamada respondia 404. O backend estava certo o tempo todo: medido com
 * o servidor de pe, /api/v1/api/v1/strategist/settings -> 404 e
 * /api/v1/strategist/settings -> 200.
 *
 * O irmao `predictiveCalendarService.ts` sempre fez do jeito certo
 * (`api.get('/predictive-calendar/...')`) — por isso o Calendario carregava e
 * as outras nao, o que mascarou o defeito.
 */

export interface BriefingItem {
  id: string;
  titulo: string;
  categoria: string;
  por_que_hoje: string;
  fonte_primaria: string;
  concluido: boolean;

  /* A ocasiao e o que ela mede — tudo opcional porque "nao medido" e um
     estado legitimo, e a tela precisa saber distinguir isso de zero. */
  ocasiao_id?: string;
  data_da_ocasiao?: string;
  dias_ate?: number;
  medida?: boolean;
  lead_time_dias?: number | null;
  lead_time_mediana_dias?: number | null;
  pico_x_base?: number | null;
  largura_dias?: number | null;
  anos_medidos?: number | null;
  series_respondentes?: number | null;
  /** Assuntos que sobem junto com esta data — para quem le poder JULGAR. */
  sobem_junto?: string | null;

  /* Publicacao ja feita (raia "ja coberto"). */
  publicado_em?: string | null;
  link?: string | null;

  /* Campos do contrato antigo, mantidos opcionais durante a migracao das
     telas. O backend novo NAO os preenche: eram texto gerado em codigo
     (volume "3M+", PAA "historico") exibido sob selo de fonte auditada. */
  horizonte?: string;
  h2s_obrigatorios?: string[];
  paa_historico?: string[];
  resumo_tldr_3_bullets?: string[];
  formato_recomendado?: string;
  schema_tipo?: string;
  monetizacao?: string;
  tempo_leitura?: string;
  volume_busca_estimado?: string;
  em_producao?: boolean;
}

export interface HorizonBlock {
  label: string;
  descricao: string;
  meta_horizonte?: number;
  itens: BriefingItem[];
}

export interface DailyBlueprintResponse {
  data_formatada: string;
  data_iso: string;
  dia_semana: string;
  semana_ano: number;
  meta_total_pautas: number;
  pautas_concluidas: number;
  progresso_pct: number;
  horizontes: {
    // Raia nova, e a mais importante: o que as esteiras JA publicaram hoje.
    // Sem ela a tela mandava produzir de novo a loteria e o horoscopo que a
    // automacao entregou as 6h da manha.
    ja_coberto?: HorizonBlock;
    giro_hoje?: HorizonBlock;
    fabrica_seo?: HorizonBlock;
    rescaldo?: HorizonBlock;
    [key: string]: HorizonBlock | undefined;
  };
  /** Fonte, janela e metodo do que a tela afirma — para poder citar a origem. */
  proveniencia?: Record<string, string>;
}

export interface CalendarEventItem {
  tipo: string;
  icone: string;
  label: string;
}

export interface CalendarOverviewResponse {
  ano: number;
  mes_numero: number;
  mes_nome: string;
  dias_eventos: Record<string, CalendarEventItem[]>;
  liquidez_mes_bi_rs: number;
  destaque_mes: string;
}

// ─── COPILOTO / CHAT (POST /api/v1/strategist/chat) ────────────────────────

export interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  suggestedAction?: SuggestedAction | null;
}

export interface SuggestedAction {
  title: string;
  category: string;
  lead_time: number;
  affiliates?: string[];
}

export interface ChatHistoryEntry {
  role: string;
  content: string;
}

export interface ChatResponse {
  status: string;
  reply: string;
  suggested_action?: SuggestedAction | null;
  planner_context?: Record<string, unknown> | null;
  timestamp: string;
}

// ─── PLANNER DE ANTECIPAÇÃO (GET /api/v1/strategist/planner) ───────────────

export interface LiquidityEvent {
  evento_liquidez: string;
  fonte_pagadora: string;
  cronograma_pagamento: string;
  mes_pico_numero: number;
  volume_injetado_bilhoes_rs: number;
  beneficiarios_estimados_milhoes: number;
  destinacao_predominante_consumo: string;
}

// dlk_ecommerce_calendario_mensal_precos: SELECT * dinâmico do DuckDB — só os
// campos que as telas realmente consomem estão modelados.
export interface EcommerceMonthData {
  segmento_dominante?: string;
  ticket_medio_rs?: number;
  inicio_pesquisa_preco?: string;
  pico_compras_transacoes?: string;
}

export interface PrescriptionItem {
  id: string;
  titulo: string;
  categoria: string;
  prioridade: string;
  impacto_estimado_rs: string;
  lead_time: string;
  descricao: string;
}

// dlk_produtos_picos_demanda_brasil: SELECT * dinâmico do DuckDB — só os
// campos que as telas realmente consomem estão modelados.
export interface ProductDemand {
  produto_nome: string;
  categoria_mercado: string;
  preco_medio_unitario_rs: number;
  epoca_pico_vendas: string;
  lead_time_antecipacao_ideal: string;
}

export interface AnticipationPlannerResponse {
  mes_numero: number;
  mes_nome: string;
  semanas_cobertas: string;
  escopo: string;
  liquidez_total_mes_bi_rs: number;
  eventos_liquidez: LiquidityEvent[];
  ecommerce_dados: EcommerceMonthData;
  produtos_destaque: ProductDemand[];
  prescricoes_estrategicas: PrescriptionItem[];
}

export interface PrescribePayload {
  title: string;
  category?: string;
  lead_time_days?: number;
  affiliates?: string[];
}

export interface PrescribeResponse {
  status: string;
  card_id: string;
  title: string;
  category: string;
  lead_time_days: number;
  affiliates_injected: string;
  target_lane: string;
  message: string;
}

// ─── KANBAN ESTRATÉGICO (GET/POST /api/v1/strategist/board) ────────────────

export interface CreateStrategicCardPayload {
  titulo: string;
  descricao?: string;
  categoria?: string;
  prioridade?: string;
  raia?: string;
  /** Liga a campanha a uma ocasião do almanaque: os prazos passam a sair da
   *  antecedência MEDIDA dela, em vez de um "D-45" escolhido no olho. */
  ocasiao_id?: string | null;
  ticket_medio_rs?: number | null;
}

export interface StrategicCard {
  id: string;
  titulo: string;
  descricao: string | null;
  categoria: string;
  prioridade: string;
  raia: string;
  ocasiao_id: string | null;
  data_da_ocasiao: string | null;
  /** Prazos retro-calculados. Nulos quando a ocasião não tem medição — prazo
   *  chutado é pior que prazo nenhum, porque o comercial planeja em cima. */
  publicar_ate: string | null;
  apresentar_ate: string | null;
  lead_time_dias: number | null;
  ticket_medio_rs: number | null;
  criado_em: string | null;
}

export interface StrategicBoardResponse {
  /** As raias vêm do SERVIDOR. Estavam escritas na tela, então acrescentar uma
   *  exigia mexer no front — e as chaves do back e do front já divergiam. */
  lanes_meta: Array<{ chave: string; label: string }>;
  columns: Record<string, StrategicCard[]>;
  total_campaigns: number;
  regras_de_prazo?: {
    dias_de_producao: number;
    dias_de_aprovacao: number;
    observacao: string;
  };
}

export interface StrategicBoardColumns {
  radar_d60: StrategicCard[];
  ignicao_d45: StrategicCard[];
  comercial_d30: StrategicCard[];
  conversao_d0: StrategicCard[];
  concluido: StrategicCard[];
}

export interface StrategistSettings {
  enabled: boolean;
  ai_model: string;
  temperature: number;
  lead_time_sensitivity: string;
  auto_create_board_cards: boolean;
  affiliate_amazon_tag?: string;
  affiliate_shopee_id?: string;
  affiliate_booking_url?: string;
  revenue_target_monthly_rs: number;
  last_sync?: string;
}

export const strategistService = {
  getDailyBlueprint: async (date?: string): Promise<DailyBlueprintResponse> => {
    const query = date ? `?date=${date}` : '';
    return apiFetch<DailyBlueprintResponse>(`/strategist/daily-blueprint${query}`);
  },

  toggleBlueprintItem: async (itemId: string, concluido: boolean): Promise<any> => {
    return apiFetch<any>(`/strategist/daily-blueprint/${itemId}/toggle?concluido=${concluido}`, {
      method: 'POST',
    });
  },

  dispatchBlueprintItem: async (itemId: string): Promise<{ success: boolean; item_id: string; status: string; message: string }> => {
    return apiFetch<{ success: boolean; item_id: string; status: string; message: string }>(
      `/strategist/daily-blueprint/dispatch-item?item_id=${itemId}`,
      { method: 'POST' }
    );
  },

  getCalendarPlannerOverview: async (ano: number, mes: number): Promise<CalendarOverviewResponse> => {
    return apiFetch<CalendarOverviewResponse>(
      `/strategist/calendar-planner-overview?ano=${ano}&mes=${mes}`
    );
  },

  chat: async (message: string, history?: ChatHistoryEntry[]): Promise<ChatResponse> => {
    return apiFetch<ChatResponse>(`/strategist/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, history }),
    });
  },

  getPlanner: async (mes?: number): Promise<AnticipationPlannerResponse> => {
    const query = mes ? `?mes=${mes}` : '';
    return apiFetch<AnticipationPlannerResponse>(`/strategist/planner${query}`);
  },

  prescribe: async (payload: PrescribePayload): Promise<PrescribeResponse> => {
    return apiFetch<PrescribeResponse>(`/strategist/prescribe`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getStrategicBoard: async (mes?: number, categoria?: string): Promise<StrategicBoardResponse> => {
    const params = new URLSearchParams();
    if (mes) params.append('mes', String(mes));
    if (categoria) params.append('categoria', categoria);
    const qs = params.toString();
    return apiFetch<StrategicBoardResponse>(`/strategist/board${qs ? `?${qs}` : ''}`);
  },

  createStrategicCard: async (payload: CreateStrategicCardPayload): Promise<{ status: string; card: StrategicCard }> => {
    return apiFetch<{ status: string; card: StrategicCard }>(`/strategist/board`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateCardLane: async (cardId: string, newLane: string): Promise<{ status: string; card: StrategicCard }> => {
    return apiFetch<{ status: string; card: StrategicCard }>(`/strategist/board/${cardId}/lane`, {
      method: 'PATCH',
      body: JSON.stringify({ new_lane: newLane }),
    });
  },

  deleteCard: async (cardId: string): Promise<{ status: string; card_id: string }> => {
    return apiFetch<{ status: string; card_id: string }>(`/strategist/board/${cardId}`, {
      method: 'DELETE',
    });
  },

  // O `?client=gnc` morreu (F-33/F-39). O backend ja ignora o parametro e tira
  // o tenant da sessao, mas enquanto o front o mandava ele viajava em toda
  // requisicao de todo cliente, aparecia em log e proxy, e ficava de armadilha:
  // bastava alguem redeclarar `client` no handler para o furo ressuscitar.
  getSettings: async (): Promise<StrategistSettings> => {
    return apiFetch<StrategistSettings>('/strategist/settings');
  },

  updateSettings: async (settings: StrategistSettings): Promise<{ status: string; settings: StrategistSettings }> => {
    return apiFetch<{ status: string; settings: StrategistSettings }>('/strategist/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
};
